<template>
  <div :class="gridClass">
    <label class="block text-sm font-medium text-gray-700">
      {{ label }} <span v-if="required" class="text-red-500">*</span>
    </label>
    <div class="py-2">
      <div class="relative">
        <!-- Barre unique avec deux curseurs -->
        <div class="relative mb-6">
          <!-- Barre de fond -->
          <div class="w-full h-2 bg-gray-200 rounded-lg relative">
            <!-- Zone sélectionnée (entre min et max) -->
            <div 
              class="absolute h-full bg-blue-500 rounded-lg transition-all duration-300"
              :style="{
                left: `${getPercentage(priceMin)}%`,
                width: `${getPercentage(priceMax) - getPercentage(priceMin)}%`
              }"
            ></div>
          </div>
          
          <!-- Curseur minimum -->
          <div 
            class="absolute top-0 w-6 h-6 bg-blue-600 rounded-full border-2 border-white shadow-lg transform -translate-y-2 cursor-pointer z-20"
            :style="{ left: `calc(${getPercentage(priceMin)}% - 12px)` }"
            @mousedown="startDragging('min')"
          ></div>
          
          <!-- Curseur maximum -->
          <div 
            class="absolute top-0 w-6 h-6 bg-blue-600 rounded-full border-2 border-white shadow-lg transform -translate-y-2 cursor-pointer z-20"
            :style="{ left: `calc(${getPercentage(priceMax)}% - 12px)` }"
            @mousedown="startDragging('max')"
          ></div>
        </div>
        
        <!-- Affichage des valeurs -->
        <div class="flex justify-between text-sm text-gray-600">
          <span class="text-blue-600 font-medium">{{ priceMin }}€</span>
          <span class="text-blue-600 font-medium">{{ priceMax }}€</span>
        </div>
        
        <!-- Affichage de la gamme sélectionnée -->
        <div class="p-3 bg-blue-50 rounded-lg">
          <p class="text-sm text-blue-800 font-medium">
            Gamme de prix sélectionnée : <span class="text-blue-600">{{ priceMin }}€ - {{ priceMax }}€</span>
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, onUnmounted } from 'vue';
import type { PriceRangeFieldProps } from '@/types/sellerRegister';

const props = withDefaults(defineProps<PriceRangeFieldProps>(), {
  required: false,
  min: 0,
  max: 200,
  step: 1,
  gridClass: ''
});

const emit = defineEmits<{
  'update:modelValue': [range: { min: number; max: number }];
}>();

const priceMin = ref(props.modelValue.min);
const priceMax = ref(props.modelValue.max);

// État du drag
const isDragging = ref(false);
const dragType = ref<'min' | 'max' | null>(null);

// Calculer le pourcentage pour le positionnement
const getPercentage = (value: number) => {
  return ((value - props.min) / (props.max - props.min)) * 100;
};

// Calculer la valeur à partir du pourcentage
const getValueFromPercentage = (percentage: number) => {
  const value = props.min + (percentage / 100) * (props.max - props.min);
  return Math.round(value / props.step) * props.step;
};

// Démarrer le drag
const startDragging = (type: 'min' | 'max') => {
  isDragging.value = true;
  dragType.value = type;
  document.addEventListener('mousemove', handleMouseMove);
  document.addEventListener('mouseup', stopDragging);
};

// Gérer le mouvement de la souris
const handleMouseMove = (event: MouseEvent) => {
  if (!isDragging.value || !dragType.value) return;
  
  const container = document.querySelector('.relative.mb-6');
  if (!container) return;
  
  const containerRect = container.getBoundingClientRect();
  const x = event.clientX - containerRect.left;
  const percentage = Math.max(0, Math.min(100, (x / containerRect.width) * 100));
  const newValue = getValueFromPercentage(percentage);
  
  if (dragType.value === 'min') {
    if (newValue < priceMax.value) {
      priceMin.value = newValue;
    }
  } else if (dragType.value === 'max') {
    if (newValue > priceMin.value) {
      priceMax.value = newValue;
    }
  }
  
  emitChange();
};

// Arrêter le drag
const stopDragging = () => {
  isDragging.value = false;
  dragType.value = null;
  document.removeEventListener('mousemove', handleMouseMove);
  document.removeEventListener('mouseup', stopDragging);
};

// Nettoyer les event listeners
onUnmounted(() => {
  document.removeEventListener('mousemove', handleMouseMove);
  document.removeEventListener('mouseup', stopDragging);
});

// Synchroniser avec le modèle
watch(() => props.modelValue, (newValue) => {
  priceMin.value = newValue.min;
  priceMax.value = newValue.max;
}, { immediate: true });

// Émettre les changements
const emitChange = () => {
  emit('update:modelValue', {
    min: priceMin.value,
    max: priceMax.value
  });
};
</script>

<style scoped>
/* Styles pour les curseurs */
.cursor-pointer {
  cursor: grab;
}

.cursor-pointer:active {
  cursor: grabbing;
}
</style>
